import type { ReactNode } from 'react';
import {
  PageShell,
  Painting,
  ManageAccessWindow,
  AgentsWindow,
  ActivityWindow,
  ConfigWindow,
  FleetWindow,
  MANAGE_ACCESS,
} from '../components';
import type { PaintingName } from '../components/Painting';
import { ProductVideo } from '../components/landing/Video';
import { LiveAccessWindow, LiveDashboardWindow, LiveGenerateWindow } from '../components/product/live';

/**
 * Unlisted: every product window and capture on one page, for pulling into
 * designs (the desktop app port, decks, docs). Components live in
 * src/components/product, demo data in product/demo.ts, captures in
 * public/media (re-shoot with .impeccable/captures/scripts).
 */

const WINDOWS: [string, string, ReactNode, PaintingName][] = [
  ['ManageAccessWindow', 'Access Control → Manage Access drawer', <ManageAccessWindow key="m" />, 'aqueduct'],
  [
    'ManageAccessWindow annotate',
    'Same, with numbered callouts',
    <ManageAccessWindow
      key="ma"
      annotate
      tools={MANAGE_ACCESS.tools.map((t, i) => ({ ...t, note: [2, undefined, 3, undefined][i] }))}
    />,
    'campagna',
  ],
  ['AgentsWindow', 'Agents table', <AgentsWindow key="a" />, 'cascade'],
  ['ActivityWindow', 'Activity feed', <ActivityWindow key="v" />, 'sunrise'],
  ['ConfigWindow', 'Config file, before and with Statio', <ConfigWindow key="c" />, 'crescenza'],
  ['FleetWindow', 'Fleet table', <FleetWindow key="f" />, 'portico'],
];

const LIVE: [string, string, ReactNode, PaintingName][] = [
  ['LiveAccessWindow', 'Deny deleteRows, then the 403 lands in Activity', <LiveAccessWindow key="la" />, 'aqueduct'],
  ['LiveDashboardWindow', 'API Calls count up, then Activity streams in', <LiveDashboardWindow key="ld" />, 'sunrise'],
  ['LiveGenerateWindow', 'Spec URL to a preview to a deployed server', <LiveGenerateWindow key="lg" />, 'campagna'],
];

const STILLS: [string, string][] = [
  ['manage-access', 'Manage Access (full screen)'],
  ['manage-access-panel', 'Manage Access (drawer crop)'],
  ['access-control', 'Access Control · By Server'],
  ['agents', 'Agents'],
  ['activity', 'Activity'],
  ['fleet', 'Fleet'],
  ['desktop-config', 'Desktop app · Configuration (old theme)'],
];

export default function KitPage() {
  return (
    <PageShell>
      <div className="s-wrap" style={{ padding: '48px var(--gutter) 80px' }}>
        <h1 style={{ font: '700 40px/1.1 var(--font-sans)', letterSpacing: '-.035em' }}>Product window kit</h1>
        <p className="l-lede" style={{ marginTop: 10 }}>
          Hand-built product windows (React, <code className="s-data">src/components/product</code>) and real captures
          from the Statio apps (<code className="s-data">public/media</code>). All names are illustrative demo data.
        </p>

        <h2 style={{ font: '700 26px/1.2 var(--font-sans)', marginTop: 48 }}>Live windows</h2>
        <p style={{ marginTop: 6, color: 'var(--ink-2)' }}>
          12-second CSS loops on the paintings, the way cursor.com animates its product. Pause button top right of each
          window; reduced motion shows the finished state.
        </p>
        <div style={{ display: 'grid', gap: 32, marginTop: 18 }}>
          {LIVE.map(([name, desc, el, art]) => (
            <figure key={name} style={{ margin: 0 }}>
              <Painting
                name={art}
                style={{
                  padding: 'clamp(16px, 5vw, 64px) clamp(16px, 6vw, 96px) clamp(40px, 7vw, 96px)',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <div style={{ width: '100%', maxWidth: 820 }}>{el}</div>
              </Painting>
              <figcaption style={{ marginTop: 8, fontSize: 14, color: 'var(--ink-2)' }}>
                <code className="s-data">&lt;{name} /&gt;</code> · {desc}
              </figcaption>
            </figure>
          ))}
        </div>

        <h2 style={{ font: '700 26px/1.2 var(--font-sans)', marginTop: 48 }}>Hand-built windows</h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(520px, 100%), 1fr))',
            gap: 24,
            marginTop: 18,
          }}
        >
          {WINDOWS.map(([name, desc, el, art]) => (
            <figure key={name} style={{ margin: 0 }}>
              <Painting
                name={art}
                style={{ padding: 36, minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <div style={{ width: '100%', maxWidth: 560 }}>{el}</div>
              </Painting>
              <figcaption style={{ marginTop: 8, fontSize: 14, color: 'var(--ink-2)' }}>
                <code className="s-data">&lt;{name} /&gt;</code> · {desc}
              </figcaption>
            </figure>
          ))}
        </div>

        <h2 style={{ font: '700 26px/1.2 var(--font-sans)', marginTop: 56 }}>Real captures</h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(520px, 100%), 1fr))',
            gap: 24,
            marginTop: 18,
          }}
        >
          <figure style={{ margin: 0 }} className="l-video">
            <div style={{ gridColumn: '1 / -1' }}>
              <ProductVideo
                name="hero-deny"
                width={400}
                height={520}
                label="Screen recording: Deny on db.deleteRows, then a 403 in Activity."
              />
            </div>
            <figcaption style={{ fontSize: 14, color: 'var(--ink-2)' }}>
              <code className="s-data">/media/hero-deny.mp4 · .webm</code> · Deny deleteRows → 403 in Activity
            </figcaption>
          </figure>
          {STILLS.map(([file, desc]) => (
            <figure key={file} style={{ margin: 0 }}>
              <img
                src={`/media/${file}.webp`}
                alt={`Statio app: ${desc} (demo data)`}
                loading="lazy"
                style={{ width: '100%', display: 'block', borderRadius: 12, boxShadow: '0 0 0 1px var(--rule)' }}
              />
              <figcaption style={{ marginTop: 8, fontSize: 14, color: 'var(--ink-2)' }}>
                <code className="s-data">/media/{file}.webp</code> · {desc}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
